import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./Preloader.module.css";

/*
 * Preloader — the once-per-session lime curtain.
 *
 *   <Preloader />        mount ONCE, in App.js. No props.
 *
 * Sequence (all CSS, 1.5s): full-viewport --lime overlay -> the wordmark fades
 * in small, centred, condensed caps in --ink -> scales up large -> the whole
 * overlay wipes UP off screen.
 *
 * Contract
 *   - Runs once per session: a sessionStorage flag (every access is wrapped in
 *     try/catch — storage can throw in private mode / blocked-cookie contexts;
 *     it then simply plays once per page load).
 *   - Never under prefers-reduced-motion: renders nothing at all.
 *   - While it is up, <html> carries the class "is-preloading". A hero holds
 *     its entrance with e.g.
 *         :global(html.is-preloading) .card { opacity: 0; transform: … }
 *     The class is added in useLayoutEffect — before the first paint and before
 *     any child's useEffect (Reveal's observers) — so the hero never flashes.
 *     It is removed the moment the wipe ends.
 *   - Can never trap the page: a hard 1.6s timeout (the design contract's
 *     ceiling — "never longer than 1.6s total") unmounts it and removes the
 *     class whatever the animation events do (no CSS, a throttled background
 *     tab, a browser that drops animationend…). Unmounting removes it too.
 *   - aria-hidden, rendered in a portal on <body> (immune to transformed or
 *     contained ancestors), z-index above the header and any modal layer.
 */

const STORAGE_KEY = "appinfigo:preloader-played";
const ROOT_CLASS = "is-preloading";
// public/index.html holds lime from the very first paint with this class (the
// bundle has not run yet, so this component cannot). The key and the gates in
// that inline script mirror STORAGE_KEY and shouldPlay() — keep them in step.
const CURTAIN_CLASS = "pre-curtain";

function liftPreCurtain() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  window.clearTimeout(window.__preCurtainTimer);
  document.documentElement.classList.remove(CURTAIN_CLASS);
}
// The contract says the preloader is NEVER up longer than 1.6s, so the cap is
// 1.6s and not a rounder number: on the path where no animation event arrives
// at all, a 2s cap held a dead lime screen 400ms past the budget. The lag
// compensation below makes the real sequence end 1.5s after mount, so the
// 100ms of slack never chops a visible wipe.
const HARD_TIMEOUT_MS = 1600;
const SEQUENCE_MS = 1500; // word 0-1000ms, wipe 940-1500ms (Preloader.module.css)
const WORDMARK = String(SITE_CONTENT.brand || "").toUpperCase();

function shouldPlay() {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return false;
    }
  } catch (error) {
    return false;
  }
  // The pre-React curtain already timed out on a slow load: the visitor has
  // stared at lime for 3s, so do not flash it a second time.
  if (window.__preCurtainExpired) {
    return false;
  }
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY) === "1") {
      return false;
    }
  } catch (error) {
    // storage unavailable: nothing to remember, play for this page load only
  }
  return true;
}

function Preloader() {
  // Decided once, in the initial state, so the very first render already knows.
  const [active, setActive] = useState(shouldPlay);
  const mountedAtRef = useRef(0);
  const lagAppliedRef = useRef(false);

  // Hand-over from the pre-React curtain. A layout effect runs before paint, so
  // when the overlay is playing it replaces the curtain in the same frame; when
  // it is not, the curtain simply lifts. Either way it can never outlive mount.
  useLayoutEffect(() => {
    liftPreCurtain();
  }, []);

  useLayoutEffect(() => {
    if (!active) {
      return undefined;
    }
    const root = document.documentElement;
    root.classList.add(ROOT_CLASS);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (error) {
      // ignore — see shouldPlay()
    }
    mountedAtRef.current = window.performance.now();
    const failSafe = window.setTimeout(() => setActive(false), HARD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(failSafe);
      root.classList.remove(ROOT_CLASS);
    };
  }, [active]);

  if (!active) {
    return null;
  }

  // A CSS animation's clock starts when its first frame is actually presented,
  // but the fail-safe counts from MOUNT. On a busy main thread those are
  // measurably 500ms+ apart (a rAF tick is not a reliable probe — one can fire
  // before the animation starts), and the fail-safe would then chop the wipe
  // off mid-flight. So when the wordmark's animation really starts, the lag is
  // handed to the CSS as a negative animation-delay: the sequence skips what
  // nobody could see and always ENDS 1.5s after mount — inside the 1.6s
  // budget and clear of the 1.6s cap. Changing animation-delay re-times a
  // running CSS animation; it does not restart it.
  const handleAnimationStart = (event) => {
    if (lagAppliedRef.current) {
      return;
    }
    lagAppliedRef.current = true;
    const lag = Math.min(
      Math.max(window.performance.now() - mountedAtRef.current, 0),
      SEQUENCE_MS
    );
    if (lag > 16) {
      event.currentTarget.style.setProperty("--preloader-lag", `${-Math.round(lag)}ms`);
    }
  };

  const handleAnimationEnd = (event) => {
    // the wordmark's own animationend bubbles up here first — ignore it; only
    // the overlay's wipe finishing ends the preload
    if (event.target === event.currentTarget) {
      setActive(false);
    }
  };

  return createPortal(
    <div
      className={styles.overlay}
      aria-hidden="true"
      onAnimationStart={handleAnimationStart}
      onAnimationEnd={handleAnimationEnd}
    >
      <span className={styles.word}>{WORDMARK}</span>
    </div>,
    document.body
  );
}

export default Preloader;
